import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const API_TOKEN = process.env.REACT_APP_API_TOKEN;

const ClienteData = ({
  cliente = "",
  identificacion = "",
  email = "",
  telefono = "",
  contacto = "",
  direccion = "",
  onClienteDataChange,
}) => {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState(cliente);
  const [open, setOpen] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [ident, setIdent] = useState(identificacion);
  const [opcionesIdentificacion, setOpcionesIdentificacion] = useState([]);
  const [dir, setDir] = useState(direccion);
  const [correo, setCorreo] = useState(email);
  const [tel, setTel] = useState(telefono);
  const [contac, setContac] = useState(contacto);
  

  useEffect(() => {
    setBusqueda(cliente);
    setIdent(identificacion);
    setCorreo(email);
    setTel(telefono);
    setContac(contacto);
    setDir(direccion);
  }, [cliente, identificacion, email, telefono, contacto, direccion]);

  useEffect(() => {
    if (!cliente || clienteSeleccionado) return;

    (async () => {
      try {
        const resp = await fetch(
          `/proformas/buscar-clientes/?q=${encodeURIComponent(cliente)}`,
          {
            headers: {
              "X-API-TOKEN": API_TOKEN,
            },
          }
        );
        const data = await resp.json();
        const lista = data.results || [];

        const encontrado = lista.find(
          (c) => c.razon_social?.toLowerCase() === cliente.toLowerCase()
        );

        if (!encontrado) return;

        const resp2 = await fetch(
          `/proformas/buscar-clientes/?id=${encontrado.id}`,
          {
            headers: {
              "X-API-TOKEN": API_TOKEN,
            },
          }
        );
        const data2 = await resp2.json();
        const d = data2.details || {};

        const opciones = [d.cedula, d.ruc].filter(Boolean);
        setOpcionesIdentificacion(opciones);
        setClienteSeleccionado(encontrado);
      } catch {}
    })();
  }, [cliente, clienteSeleccionado]);

  useEffect(() => {
    if (!busqueda || busqueda.length < 2) {
      if (!clienteSeleccionado) {
        setClientes([]);
        setOpen(false);
      }
      return;
    }

    if (clienteSeleccionado?.razon_social === busqueda) {
      setOpen(false);
      return;
    }

    let active = true;
    (async () => {
      try {
        const resp = await fetch(
          `/proformas/buscar-clientes/?q=${encodeURIComponent(busqueda)}`,
          {
            headers: {
              "X-API-TOKEN": API_TOKEN,
            },
          }
        );
        const data = await resp.json();
        if (active) {
          setClientes(data.results || []);
          setOpen((data.results || []).length > 0);
        }
      } catch {}
    })();

    return () => {
      active = false;
    };
  }, [busqueda, clienteSeleccionado]);

  useEffect(() => {
    onClienteDataChange?.({
      cliente: busqueda,
      identificacion_cliente: ident,
      direccion: dir,
      email: correo,
      telefono: tel,
      contacto: contac,
    });
  }, [busqueda, ident, dir, correo, tel, contac]);

  const cargarCliente = async (cliente) => {
    if (!cliente) return;
    try {
      const resp = await fetch(
        `/proformas/buscar-clientes/?id=${cliente.id}`,
        {
          headers: {
            "X-API-TOKEN": API_TOKEN,
          },
        }
      );
      const data = await resp.json();
      const d = data.details || {};

      setClienteSeleccionado(cliente);
      setBusqueda(d.razon_social || "");

      const opciones = [d.cedula, d.ruc].filter(Boolean);
      setOpcionesIdentificacion(opciones);

      setIdent(identificacion || opciones[0] || "");
      setDir(d.direccion || "");
      setCorreo(d.correo || "");
      setTel(d.telefono || "");
    } catch {
    } finally {
      setOpen(false);
    }
  };

  const limpiarCampos = () => {
    setBusqueda("");
    setClientes([]);
    setOpen(false);
    setClienteSeleccionado(null);
    setIdent("");
    setOpcionesIdentificacion([]);
    setDir("");
    setCorreo("");
    setTel("");
    setContac("");
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Datos del Cliente
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={clientes}
            getOptionLabel={(opt) => opt.razon_social || ""}
            inputValue={busqueda}
            onInputChange={(_, v) => setBusqueda(v)}
            onChange={(_, v) => cargarCliente(v)}
            open={open}
            onClose={() => setOpen(false)}
            noOptionsText="No se encontraron resultados"
            freeSolo
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ingrese nombre o razón social"
                fullWidth
                onBlur={() => setOpen(false)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={limpiarCampos}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            select
            label="Identificación"
            value={ident}
            onChange={(e) => setIdent(e.target.value)}
            fullWidth
          >
            {opcionesIdentificacion.map((op, i) => (
              <MenuItem key={i} value={op}>
                {op}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Dirección"
            value={dir}
            onChange={(e) => setDir(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Email"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Contacto (Opcional)"
            value={contac}
            onChange={(e) => setContac(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Teléfono"
            value={tel}
            onChange={(e) => setTel(e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClienteData;
